import { createContext, use, useState, type ReactNode } from 'react';

import { useTranslations } from './libraries/i18n/useTranslations';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getAppSettingsOptions, type ApplicationTypesViewModel } from '@generated';
import { useIdentity } from './features/authentication/identity/IdentityContext';
import { useNavigate } from 'react-router-dom';
import { useGetClosestHandle } from './shared/routing/useGetClosestHandle';

export interface AppSettingsContextModel extends ApplicationTypesViewModel {
	showLegacyApp: boolean;
	setShowLegacyApp: (value: boolean) => void;
}

const AppSettingsContext = createContext<AppSettingsContextModel | null>(null);

export function AppSettingsContextProvider({ children }: { children: ReactNode }) {
	const navigate = useNavigate();
	const { userIsAnAdmin } = useIdentity();
	const routeHandle = useGetClosestHandle<{ legacyRoute?: boolean }>();
	const { data } = useSuspenseQuery({
		...getAppSettingsOptions(),
		staleTime: 0,
		gcTime: 0
	});

	const [showLegacyApp, setShowLegacyApp] = useState(routeHandle?.legacyRoute || !userIsAnAdmin());

	const onShowLegacyAppChange = (value: boolean) => {
		setShowLegacyApp(value);
		navigate('/');
	};

	useTranslations('en');

	return <AppSettingsContext value={{ ...data, showLegacyApp, setShowLegacyApp: onShowLegacyAppChange }}>{children}</AppSettingsContext>;
}

export const useAppSettings = () => {
	const context = use(AppSettingsContext);
	if (!context) {
		throw new Error('useAppSettings must be used within a AppSettingsContextProvider');
	}
	return context;
};
